import { FC, useEffect, useRef, useState } from 'react';

import { MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useGetApiArbitrageDetail } from '@/api/arbitrage/arbitrage';
import { GoBackArrowBtn } from '@/components/GoBackArrowBtn/GoBackArrowBtn';
import { Loader } from '@/components/Loader/loader';
import { ROUTE } from '@/constants/route';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';

import styles from './styles.module.css';

type ArbitrageSignatureState = { url: string };

const isArbitrageSignatureState = (s: unknown): s is ArbitrageSignatureState =>
  typeof s === 'object' && s !== null && typeof (s as Record<string, unknown>).url === 'string';

const ArbitrageSignature: FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const locationState: ArbitrageSignatureState | null = isArbitrageSignatureState(state) ? state : null;

  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const hasTrackedRef = useRef(false);

  const { data, isLoading, isError } = useGetApiArbitrageDetail(id ?? '');

  useEffect(() => {
    if (hasTrackedRef.current || !locationState?.url || isError || isLoading) return;
    hasTrackedRef.current = true;
    TRACKING_INSTANCE.setTcVars({
      page_nom: 'signature_electronique',
      page_template: 'espace client',
    });
    void TRACKING_INSTANCE.triggerEvent('page', undefined, {
      event_id: 'page',
      tunnel_produit: 'tunnel_unique',
      event_categorie: 'affichage espace client',
      event_nom: 'signature_electronique',
      event_page: 'signature_electronique',
    });
  }, [locationState?.url, isError, isLoading]);

  if (!locationState?.url) return <Navigate to={ROUTE.ERREUR} replace />;
  if (isError) return <Navigate to={ROUTE.ERREUR} replace />;
  if (isLoading) return <Loader />;

  const montantArbitre =
    data?.supports?.supportsDesinvestis?.reduce((acc, s) => acc + (s.montantDesinvesti ?? 0), 0) ?? 0;

  return (
    <div className={styles.container}>
      {!isIframeLoaded && <Loader />}

      <MtxGrid row className={styles.header}>
        <MtxGrid col={12} colXs={12} colSm={12} colMd={12} colLg={12} colXl={12}>
          <GoBackArrowBtn goBack={-1} alternative />

          <div className={styles.header}>
            <h1>{t('arbitrageSignature.pageTitle')}</h1>
            <p>{t('arbitrageSignature.subtitle')}</p>
          </div>
        </MtxGrid>
      </MtxGrid>

      <MtxGrid row className={styles.recap}>
        <MtxGrid
          col={12}
          offset={0}
          colXs={12}
          offsetXs={0}
          colSm={10}
          offsetSm={1}
          colMd={10}
          offsetMd={1}
          colLg={6}
          offsetLg={3}
          colXl={6}
          offsetXl={3}
        >
          <span className={styles.tableTitle}>{t('arbitrageSignature.summary.title')}</span>

          <table className={styles.contractDetailsTable}>
            <tbody className={styles.contractDetailsList}>
              <tr className={styles.contractDetailLine}>
                <th scope="row" className={styles.contractDetailsTitle}>
                  <p>{t('arbitrageSignature.summary.contractName')}</p>
                </th>
                <td className={styles.contractDetailsValue}>
                  <p>{data?.Name}</p>
                </td>
              </tr>
              <tr className={styles.contractDetailLine}>
                <th scope="row" className={styles.contractDetailsTitle}>
                  <p>{t('arbitrageSignature.summary.montantArbitre')}</p>
                </th>
                <td className={styles.contractDetailsValue}>
                  <p>{t('format.currencyFlexible', { value: montantArbitre })}</p>
                </td>
              </tr>
              <tr className={styles.contractDetailLine}>
                <th scope="row" className={styles.contractDetailsTitle}>
                  <p>{t('arbitrageSignature.summary.profilInvestisseur')}</p>
                </th>
                <td className={styles.contractDetailsValue}>
                  <p>{data?.profilChoisi}</p>
                </td>
              </tr>
              <tr className={styles.contractDetailLine}>
                <th scope="row" className={styles.contractDetailsTitle}>
                  <p>{t('arbitrageSignature.summary.profilAllocation')}</p>
                </th>
                <td className={styles.contractDetailsValue}>
                  <p>
                    {data?.supports?.newSrri == null
                      ? '–'
                      : `${t('format.numberFlexibleTenth', { value: data.supports.newSrri })}/7`}
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </MtxGrid>
      </MtxGrid>

      <MtxGrid row>
        <MtxGrid
          col={12}
          offset={0}
          colXs={12}
          offsetXs={0}
          colSm={12}
          offsetSm={0}
          colMd={10}
          offsetMd={1}
          colLg={8}
          offsetLg={2}
          colXl={8}
          offsetXl={2}
        >
          <iframe
            className={styles.signatureIframe}
            src={locationState.url}
            width="100%"
            title={t('arbitrageSignature.pageTitle')}
            onLoad={() => setIsIframeLoaded(true)}
            onError={() => navigate(ROUTE.ERREUR, { replace: true })}
          />
        </MtxGrid>
      </MtxGrid>
    </div>
  );
};

export default ArbitrageSignature;
export type { ArbitrageSignatureState };
